import { Button } from "@/components/ui/button";
import { CustomerModel } from "@/models/pipeline";
import { UnderwritingQueueClaim } from "@/services/UnderwritingService";
import { formatQueueCustomerAge, queueCustomerAge } from "@/helpers/underwritingQueueDashboard";
import { ClaimConfirmDialog } from "./ClaimConfirmDialog";

const BODY_CELL_CLASS = "px-4 py-3 whitespace-nowrap";

/** A claim goes stale (eligible for "Claim Over") after this long. */
const STALE_CLAIM_MINUTES = 20;

interface UnderwritingQueueCustomerRowProps {
  customer: CustomerModel;
  claim?: UnderwritingQueueClaim | null;
  currentUserName?: string | null;
  onClick: (customer: CustomerModel) => void;
  onClaim?: (customer: CustomerModel) => Promise<void>;
}

export function UnderwritingQueueCustomerRow({ customer, claim, currentUserName, onClick, onClaim }: Readonly<UnderwritingQueueCustomerRowProps>) {
  const isStale =
    claim?.claimedOn != null && Date.now() - new Date(claim.claimedOn).getTime() > STALE_CLAIM_MINUTES * 60_000;
  const isMine = claim?.claimedBy != null && currentUserName != null && claim.claimedBy === currentUserName;

  return (
    <tr
      onClick={() => onClick(customer)}
      className="cursor-pointer border-b border-border transition-colors hover:bg-slate-100 last:border-0"
    >
      <td className={`${BODY_CELL_CLASS} text-foreground`}>{customer.applicationId}</td>
      <td className={BODY_CELL_CLASS}>
        <span className="font-medium text-slate-700">{customer.customerName || "—"}</span>
      </td>
      <td className={`${BODY_CELL_CLASS} text-muted-foreground`}>—</td>
      <td className={`${BODY_CELL_CLASS} text-muted-foreground`}>
        {formatQueueCustomerAge(queueCustomerAge(customer))}
      </td>
      <td className={BODY_CELL_CLASS} onClick={(e) => e.stopPropagation()}>
        {isMine ? (
          <span className="text-[11px] font-medium text-purple-600">You</span>
        ) : claim?.claimedBy && !isStale ? (
          <span className="text-[11px] text-muted-foreground">{claim.claimedBy}</span>
        ) : (
          <div className={isStale ? "flex flex-col items-start gap-1" : undefined}>
            {isStale && claim?.claimedBy && (
              <span className="text-[11px] text-muted-foreground">{claim.claimedBy}</span>
            )}
            <ClaimConfirmDialog
              trigger={
                <Button
                  size="sm"
                  variant="outline"
                  className={
                    isStale
                      ? "h-6 w-24 text-[11px] px-2 border-amber-400 text-slate-700 hover:bg-amber-100"
                      : "h-6 w-24 text-[11px] px-2 border-purple-600 text-slate-700 hover:bg-purple-100"
                  }
                >
                  {isStale ? "Claim Over" : "Claim"}
                </Button>
              }
              onConfirm={onClaim ? () => onClaim(customer) : undefined}
              title={isStale ? "Override Claim" : "Claim Application"}
              description={
                isStale
                  ? `This application has been held by ${claim?.claimedBy} for over ${STALE_CLAIM_MINUTES} minutes. Do you want to take over and assign it to yourself?`
                  : "Please confirm you want to claim this application. It will be assigned to you and removed from the available queue."
              }
              confirmLabel={isStale ? "Take Over" : undefined}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

